import React, { useState, useEffect } from 'react';
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatUnits, parseUnits } from 'viem';
import { FaShieldAlt, FaChartLine, FaLock, FaDollarSign, FaExchangeAlt, FaWallet, FaCoins } from 'react-icons/fa';
import axios from 'axios';
import '../styles/VaultV3.css';
import { vaultConfig } from '../config/vaultConfig-v2';
import { useVaultAPYv2 } from '../hooks/useVaultAPYv2';
import VaultAllocationsV2 from '../components/VaultAllocationsV2';
import UserEarningsV2 from '../components/UserEarningsV2';
import AdapterHealth from '../components/AdapterHealth';

function VaultV3() {
  const { address, isConnected } = useAccount();
  const [activeTab, setActiveTab] = useState('deposit');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [avaxPrice, setAvaxPrice] = useState(42.50);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Use the enhanced APY hook
  const { apy: vaultApy } = useVaultAPYv2();

  // Read vault data
  const { data: totalAssets } = useReadContract({
    ...vaultConfig.vaultCore,
    functionName: 'totalAssets',
    watch: true,
  });

  const { data: totalSupply } = useReadContract({
    ...vaultConfig.vaultCore,
    functionName: 'totalSupply',
    watch: true,
  });

  const { data: userShares } = useReadContract({
    ...vaultConfig.vaultCore,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    watch: true,
    enabled: !!address,
  });

  const { data: depositCap } = useReadContract({
    ...vaultConfig.vaultCore,
    functionName: 'maxTotalAssets',
    watch: true,
  });

  const { data: emergencyMode } = useReadContract({
    ...vaultConfig.vaultCore,
    functionName: 'paused',
    watch: true,
  });

  // Fetch deposit and withdraw fees
  const { data: depositFeeBps = 0n } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'depositFeeBps',
    watch: true,
  });

  const { data: withdrawFeeBps = 0n } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'withdrawFeeBps',
    watch: true,
  });

  // Read USDC balance
  const { data: usdcBalance } = useReadContract({
    address: vaultConfig.usdc.address,
    abi: vaultConfig.usdc.abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    watch: true,
    enabled: !!address,
  });

  // Read USDC allowance
  const { data: usdcAllowance } = useReadContract({
    address: vaultConfig.usdc.address,
    abi: vaultConfig.usdc.abi,
    functionName: 'allowance',
    args: address ? [address, vaultConfig.vaultCore.address] : undefined,
    watch: true,
    enabled: !!address,
  });

  // Write contracts
  const { writeContract: approveUsdc, data: approveHash } = useWriteContract();
  const { writeContract: deposit, data: depositHash } = useWriteContract();
  const { writeContract: withdraw, data: withdrawHash } = useWriteContract();

  // Wait for transactions with success callbacks
  const { isLoading: isApproving, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({ 
    hash: approveHash 
  });
  const { isLoading: isDepositing, isSuccess: isDepositSuccess } = useWaitForTransactionReceipt({ 
    hash: depositHash 
  });
  const { isLoading: isWithdrawing, isSuccess: isWithdrawSuccess } = useWaitForTransactionReceipt({ 
    hash: withdrawHash 
  });

  // Calculate values
  // Share price: How much USDC (6 decimals) per vUSDC (6 decimals)
  // Since both are 6 decimals, the ratio is 1:1 in decimals
  const sharePrice = totalSupply && totalSupply > 0n && totalAssets 
    ? (totalAssets * 1000000n) / totalSupply 
    : 1000000n;  // 1:1 default

  const userPosition = userShares && totalAssets && totalSupply && totalSupply > 0n
    ? (userShares * totalAssets) / totalSupply
    : 0n;

  const utilizationRate = totalAssets && depositCap && depositCap > 0n
    ? (Number(totalAssets) / Number(depositCap)) * 100
    : 0;

  // Format values for display
  const tvl = totalAssets ? parseFloat(formatUnits(totalAssets, 6)) : 0;
  const sharePriceFormatted = sharePrice ? parseFloat(formatUnits(sharePrice, 6)) : 1.0;  // Share price in 6 decimals
  const userSharesFormatted = userShares ? parseFloat(formatUnits(userShares, 6)) : 0;  // vUSDC is 6 decimals
  const userPositionFormatted = userPosition ? parseFloat(formatUnits(userPosition, 6)) : 0;
  const usdcBalanceFormatted = usdcBalance ? parseFloat(formatUnits(usdcBalance, 6)) : 0;
  const depositCapFormatted = depositCap ? parseFloat(formatUnits(depositCap, 6)) : 0;

  // Fetch AVAX price
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:436';
        const priceResponse = await axios.get(`${apiUrl}/prices`);
        if (priceResponse.data && priceResponse.data.avax) {
          setAvaxPrice(priceResponse.data.avax.priceUsd);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Clear processing state and input fields when transactions complete
  useEffect(() => {
    if (isDepositSuccess) {
      setIsProcessing(false);
      setDepositAmount('');
    }
  }, [isDepositSuccess]);

  useEffect(() => {
    if (isWithdrawSuccess) {
      setIsProcessing(false);
      setWithdrawAmount('');
    }
  }, [isWithdrawSuccess]);

  // Handle deposit
  const handleDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) return;
    
    setIsProcessing(true);
    try {
      const amount = parseUnits(depositAmount, 6);
      
      // Check if approval is needed
      if (!usdcAllowance || usdcAllowance < amount) {
        await approveUsdc({
          address: vaultConfig.usdc.address,
          abi: vaultConfig.usdc.abi,
          functionName: 'approve',
          args: [vaultConfig.vaultCore.address, amount],
        });
      }
      
      // Deposit
      await deposit({
        ...vaultConfig.vaultCore,
        functionName: 'deposit',
        args: [amount, address],
      });
      
      setDepositAmount('');
    } catch (error) {
      console.error('Deposit error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle withdraw
  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    
    setIsProcessing(true);
    try {
      const shares = parseUnits(withdrawAmount, 6);  // vUSDC is 6 decimals
      
      // Redeem shares for USDC
      await withdraw({
        ...vaultConfig.vaultCore,
        functionName: 'redeem',
        args: [shares, address, address],
      });
      
      setWithdrawAmount('');
    } catch (error) {
      console.error('Withdraw error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="vault-v3-container">
      {/* Header */}
      <div className="vault-header">
        <div className="vault-title">
          <FaShieldAlt />
          <div>
            <h1>Velvet USD Vault</h1>
            <p>Automated yield optimization across DeFi protocols</p>
          </div>
        </div>
        {emergencyMode && (
          <div className="emergency-banner">
            Emergency Mode Active - Deposits Paused
          </div>
        )}
      </div>

      {/* Main Content - Metrics and Forms Side by Side */}
      <div className="vault-main-content">
        {/* Left Side - Vertical Metrics */}
        <div className="metrics-vertical">
          <div className="metric-card">
            <div className="metric-icon">
              <FaChartLine />
            </div>
            <div className="metric-content">
              <span className="metric-label">Current APY</span>
              <span className="metric-value highlight">
                {vaultApy ? `${vaultApy.current.toFixed(2)}%` : '8.5%'}
              </span>
              <span className="metric-subtext">
                7d: {vaultApy ? `${vaultApy.avg7d.toFixed(2)}%` : '8.2%'}
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <FaDollarSign />
            </div>
            <div className="metric-content">
              <span className="metric-label">Total Value Locked</span>
              <span className="metric-value">
                ${tvl.toLocaleString()}
              </span>
              <span className="metric-subtext">
                {(tvl / avaxPrice).toFixed(2)} AVAX
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <FaExchangeAlt />
            </div>
            <div className="metric-content">
              <span className="metric-label">Share Price</span>
              <span className="metric-value">
                ${sharePriceFormatted.toFixed(6)}
              </span>
              <span className="metric-subtext">
                1 vUSDC = {sharePriceFormatted.toFixed(6)} USDC
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">
              <FaLock />
            </div>
            <div className="metric-content">
              <span className="metric-label">Deposit Cap</span>
              <span className="metric-value">
                ${depositCapFormatted.toLocaleString()}
              </span>
              <div className="utilization-bar">
                <div 
                  className="utilization-fill" 
                  style={{ 
                    width: `${Math.min(utilizationRate, 100)}%`,
                    background: utilizationRate > 90 ? '#ef4444' : utilizationRate > 70 ? '#f59e0b' : '#10b981'
                  }}
                />
              </div>
              <span className="metric-subtext">
                {utilizationRate.toFixed(1)}% Utilized
              </span>
            </div>
          </div>
        </div>

        {/* Right Side - Deposit/Withdraw Form */}
        <div className="action-panel">
          <div className="action-tabs">
            <button 
              className={`tab-btn ${activeTab === 'deposit' ? 'active' : ''}`}
              onClick={() => setActiveTab('deposit')}
              disabled={emergencyMode}
            >
              Deposit
            </button>
            <button 
              className={`tab-btn ${activeTab === 'withdraw' ? 'active' : ''}`}
              onClick={() => setActiveTab('withdraw')}
            >
              Withdraw
            </button>
          </div>

          {!isConnected ? (
            <div className="connect-prompt">
              <FaShieldAlt size={48} />
              <h3>Connect Your Wallet</h3>
              <p>Connect your wallet to interact with the vault</p>
            </div>
          ) : (
            <div className="form-content">
              {activeTab === 'deposit' && !emergencyMode && (
                <div className="deposit-form">
                  <div className="balance-display">
                    <span>Balance: {usdcBalanceFormatted.toFixed(2)} USDC</span>
                  </div>

                  <div className="input-group">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      className="amount-input"
                    />
                    <span className="input-token">USDC</span>
                    <button 
                      className="max-btn"
                      onClick={() => setDepositAmount(usdcBalanceFormatted.toString())}
                    >
                      MAX
                    </button>
                  </div>

                  <div className="estimate-info">
                    <span>You will receive</span>
                    <span className="estimate-value">
                      ~{depositAmount ? (
                        parseFloat(depositAmount) * (1 - Number(depositFeeBps) / 10000) / sharePriceFormatted
                      ).toFixed(4) : '0.0000'} vUSDC
                    </span>
                  </div>
                  
                  {depositFeeBps > 0n && depositAmount && parseFloat(depositAmount) > 0 && (
                    <div className="fee-info" style={{ marginTop: '10px', fontSize: '0.9em', color: '#ff9800' }}>
                      <span>Deposit Fee ({(Number(depositFeeBps) / 100).toFixed(2)}%): </span>
                      <span>{(parseFloat(depositAmount) * Number(depositFeeBps) / 10000).toFixed(2)} USDC</span>
                    </div>
                  )}

                  <button 
                    className="action-btn deposit-btn"
                    onClick={handleDeposit}
                    disabled={isProcessing || isApproving || isDepositing || !depositAmount || parseFloat(depositAmount) <= 0}
                  >
                    {isApproving ? 'Approving...' : isDepositing ? 'Depositing...' : 'Deposit USDC'}
                  </button>

                  <div className="form-info">
                    <p>• {depositFeeBps > 0n ? `${(Number(depositFeeBps) / 100).toFixed(2)}% deposit fee` : 'No deposit fees'}</p>
                    <p>• Earn {vaultApy?.current.toFixed(2) || '8.5'}% APY</p>
                    <p>• Withdraw anytime</p>
                  </div>
                </div>
              )}

              {activeTab === 'withdraw' && (
                <div className="withdraw-form">
                  <div className="balance-display">
                    <span>Balance: {userSharesFormatted.toFixed(4)} vUSDC</span>
                  </div>

                  <div className="input-group">
                    <input
                      type="number"
                      placeholder="0.0000"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className="amount-input"
                    />
                    <span className="input-token">vUSDC</span>
                    <button 
                      className="max-btn"
                      onClick={() => setWithdrawAmount(userSharesFormatted.toString())}
                    >
                      MAX
                    </button>
                  </div>

                  <div className="estimate-info">
                    <span>You will receive</span>
                    <span className="estimate-value">
                      ~{withdrawAmount ? (
                        parseFloat(withdrawAmount) * sharePriceFormatted * (1 - Number(withdrawFeeBps) / 10000)
                      ).toFixed(2) : '0.00'} USDC
                    </span>
                  </div>
                  
                  {withdrawFeeBps > 0n && withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                    <div className="fee-info" style={{ marginTop: '10px', fontSize: '0.9em', color: '#ff9800' }}>
                      <span>Withdraw Fee ({(Number(withdrawFeeBps) / 100).toFixed(2)}%): </span>
                      <span>{(parseFloat(withdrawAmount) * sharePriceFormatted * Number(withdrawFeeBps) / 10000).toFixed(2)} USDC</span>
                    </div>
                  )}

                  <button 
                    className="action-btn withdraw-btn"
                    onClick={handleWithdraw}
                    disabled={isProcessing || isWithdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                  >
                    {isWithdrawing ? 'Withdrawing...' : 'Withdraw to USDC'}
                  </button>

                  <div className="form-info">
                    <p>• {withdrawFeeBps > 0n ? `${(Number(withdrawFeeBps) / 100).toFixed(2)}% withdrawal fee` : 'No withdrawal fees'}</p>
                    <p>• Instant withdrawal</p>
                    <p>• Receive USDC directly</p>
                  </div>
                </div>
              )}

              {activeTab === 'deposit' && emergencyMode && (
                <div className="emergency-message">
                  <h3>Deposits Temporarily Paused</h3>
                  <p>The vault is in emergency mode. Withdrawals are still available.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Position - directly below deposit/withdraw section */}
      {isConnected && <UserEarningsV2 key={`earnings-${isDepositSuccess}-${isWithdrawSuccess}`} />}

      {/* Additional Components - Pass key to force re-render on transaction success */}
      <VaultAllocationsV2 key={`allocations-${isDepositSuccess}-${isWithdrawSuccess}`} />
      <AdapterHealth key={`health-${isDepositSuccess}-${isWithdrawSuccess}`} />
    </div>
  );
}

export default VaultV3;